import { Command as CommandPrimitive } from 'cmdk'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#app/utils/misc.tsx'
import { Command, CommandInput, CommandList } from './command.tsx'
import { Icon } from './icon.tsx'
import { Popover, PopoverContent, PopoverTrigger } from './popover.tsx'
import { Skeleton } from './skeleton.tsx'

/**
 * Combobox — a generic single-select searchable dropdown, the gap `Select`
 * (no search) and `TagInput` (multi-select, free entry) don't cover. It composes
 * the existing cmdk `Command` matcher/list (type-to-filter, arrow-key nav, Enter
 * to select) inside the shared `Popover` substrate (outside-click / `Esc`
 * dismissal, anchored motion).
 *
 * The trigger mirrors the `Select` metrics exactly — same `--input` border,
 * `bg-background`, `h-8`, `focus-cosy` ring, and the `aria-invalid` →
 * `--input-invalid` contract — but carries a **double-chevron** glyph to
 * distinguish it from Select's single chevron, and is a `role="combobox"` whose
 * `aria-expanded` tracks the panel. The panel is the Popover chrome with zero
 * padding: the `CommandInput` search row (magnifying-glass glyph + borderless
 * input, hairline below) over a listbox — each option `role="option"`, the
 * hover / keyboard-highlight painting `--bg-hover`, the chosen value trailing a
 * `--brand` check. Options may carry an optional leading avatar/meta node.
 *
 * `loading` swaps the list for two Skeleton rows (so an in-flight fetch reads as
 * "loading", not "empty"); the empty state echoes the typed query. Controlled
 * only — pass `value` + `onValueChange`. Tokens only.
 */

export type ComboboxItem = {
	/** The stable value reported via `onValueChange` and matched for the check. */
	value: string
	/** The visible label (also what type-to-filter matches on). */
	label: string
	/** Optional secondary meta line under the label. */
	description?: string
	/** Optional leading node (an Avatar, an Icon tile, …). */
	avatar?: React.ReactNode
}

export type ComboboxProps = {
	/** Controlled selected value (`''`/`undefined` = nothing chosen). */
	value?: string
	/** Fires with the newly chosen item's value. */
	onValueChange?: (value: string) => void
	/** The selectable options. */
	items: ComboboxItem[]
	/** Resting trigger label when nothing is chosen. @default t('combobox.placeholder') */
	placeholder?: string
	/** While true, the panel shows Skeleton rows instead of the list. @default false */
	loading?: boolean
	/** Overrides the no-match message (which otherwise echoes the query). */
	emptyText?: string
	/** Render with the panel initially open (uncontrolled) — for the specimen. @default false */
	defaultOpen?: boolean
	/** Disable the trigger (Select's disabled treatment). @default false */
	disabled?: boolean
	/** Marks the trigger invalid — repaints the border with `--input-invalid`. */
	'aria-invalid'?: boolean
	/** Accessible name for the trigger. */
	'aria-label'?: string
	'aria-labelledby'?: string
	id?: string
	className?: string
}

/** How many Skeleton placeholder rows the loading state paints. */
const LOADING_ROWS = 2

function Combobox({
	value,
	onValueChange,
	items,
	placeholder,
	loading = false,
	emptyText,
	defaultOpen = false,
	disabled = false,
	className,
	id,
	'aria-invalid': ariaInvalid,
	'aria-label': ariaLabel,
	'aria-labelledby': ariaLabelledby,
}: ComboboxProps) {
	const { t } = useTranslation()
	const [open, setOpen] = React.useState(defaultOpen)
	const [query, setQuery] = React.useState('')

	const selected = items.find((item) => item.value === value)

	function handleOpenChange(next: boolean) {
		setOpen(next)
		// Reset the search when the panel closes so it reopens clean.
		if (!next) setQuery('')
	}

	function handleSelect(item: ComboboxItem) {
		onValueChange?.(item.value)
		handleOpenChange(false)
	}

	return (
		<Popover open={open} onOpenChange={handleOpenChange}>
			<PopoverTrigger asChild>
				<button
					type="button"
					// Canonical combobox pattern: the trigger is the combobox and its
					// popup is a listbox (Radix supplies aria-expanded / aria-controls).
					role="combobox"
					aria-haspopup="listbox"
					aria-invalid={ariaInvalid}
					aria-label={ariaLabel}
					aria-labelledby={ariaLabelledby}
					id={id}
					disabled={disabled}
					data-slot="combobox-trigger"
					className={cn(
						// Mirror the Select trigger surface exactly.
						'focus-cosy border-input bg-background aria-[invalid]:border-input-invalid text-body-sm md:text-body-xs flex h-8 w-full items-center justify-between gap-2 rounded-md border px-3 py-1.5 outline-hidden disabled:cursor-not-allowed disabled:opacity-50 [&>span]:truncate',
						className,
					)}
				>
					<span className={cn(!selected && 'text-muted-foreground')}>
						{selected?.label ?? placeholder ?? t('combobox.placeholder')}
					</span>
					<Icon
						name="chevron-down"
						className="text-muted-foreground size-4 shrink-0 opacity-80"
						aria-hidden
					/>
				</button>
			</PopoverTrigger>
			<PopoverContent
				align="start"
				data-slot="combobox-content"
				// Name the popup dialog after the field so it isn't an anonymous
				// `role="dialog"` (axe `aria-dialog-name`).
				aria-label={ariaLabel}
				aria-labelledby={ariaLabelledby}
				className="w-[var(--radix-popover-trigger-width)] overflow-hidden p-0"
			>
				<Command shouldFilter={!loading}>
					<CommandInput
						value={query}
						onValueChange={setQuery}
						placeholder={t('combobox.search')}
					/>
					{loading ? (
						<ComboboxLoading label={t('combobox.loading')} />
					) : (
						<CommandList>
							<CommandPrimitive.Empty
								data-slot="combobox-empty"
								className="text-muted-foreground text-body-sm px-3 py-6 text-center"
							>
								{emptyText ?? t('combobox.noResults', { query })}
							</CommandPrimitive.Empty>
							{items.map((item) => (
								<ComboboxOption
									key={item.value}
									item={item}
									chosen={item.value === value}
									onSelect={() => handleSelect(item)}
								/>
							))}
						</CommandList>
					)}
				</Command>
			</PopoverContent>
		</Popover>
	)
}

/**
 * A single option row. cmdk supplies `role="option"` and drives the keyboard /
 * hover highlight via `data-selected`, which we paint with `--bg-hover`; the
 * chosen value (not the highlight) shows the trailing `--brand` check. `value`
 * feeds cmdk's identity, `keywords` feed type-to-filter on the visible label.
 */
function ComboboxOption({
	item,
	chosen,
	onSelect,
}: {
	item: ComboboxItem
	chosen: boolean
	onSelect: () => void
}) {
	return (
		<CommandPrimitive.Item
			value={item.value}
			keywords={[item.label, item.description ?? '']}
			onSelect={onSelect}
			data-slot="combobox-item"
			data-chosen={chosen || undefined}
			className="text-body-sm relative flex cursor-default items-center gap-2 rounded-md px-2 py-2 outline-hidden select-none data-[selected=true]:bg-[var(--bg-hover)]"
		>
			{item.avatar ? (
				<span className="flex shrink-0 items-center">{item.avatar}</span>
			) : null}
			<span className="flex min-w-0 flex-col">
				<span className="truncate">{item.label}</span>
				{item.description ? (
					<span className="text-muted-foreground text-body-xs truncate">
						{item.description}
					</span>
				) : null}
			</span>
			{chosen ? (
				<Icon name="check" className="text-brand ml-auto size-4 shrink-0" />
			) : null}
		</CommandPrimitive.Item>
	)
}

/**
 * Loading state: two Skeleton rows mimicking option rows, wrapped in a
 * `role="status"` region so assistive tech announces the busy state instead of
 * reading the list as empty. Non-interactive; tokens only.
 */
function ComboboxLoading({ label }: { label: string }) {
	return (
		<div
			data-slot="combobox-loading"
			role="status"
			aria-busy="true"
			aria-label={label}
			className="flex flex-col gap-1 p-1"
		>
			{Array.from({ length: LOADING_ROWS }).map((_, i) => (
				<div
					key={i}
					className="flex items-center gap-2 rounded-md px-2 py-2"
					aria-hidden="true"
				>
					<Skeleton className="size-5 shrink-0 rounded-full" />
					<Skeleton className="h-4 w-2/3" />
				</div>
			))}
		</div>
	)
}

export { Combobox }
